import {z} from "zod";
import type {Money} from "./money.ts";

/**
 * The coach's mood is computed from real account state, never invented by the
 * model. It drives the mascot expression and the tone of generated prose.
 */
export const financialMoods = ["thriving", "content", "neutral", "watchful", "concerned", "alarmed"] as const;
export const financialMoodSchema = z.enum(financialMoods);
export type FinancialMood = z.infer<typeof financialMoodSchema>;

export interface MoodInput {
  /** Income for the current period, minor units, >= 0. */
  incomeMinor: number;
  /** Spending for the current period, minor units, >= 0. */
  expenseMinor: number;
  /** Spending for the comparison period, minor units, >= 0. Null when unknown. */
  previousExpenseMinor: number | null;
  /** Accounts currently failing to sync. */
  accountsFailed: number;
  /** Connected, non-disconnected accounts. */
  accountsTotal: number;
}

export interface MoodAssessment {
  mood: FinancialMood;
  headline: string;
  detail: string;
  savingsRate: number | null;
  /** Positive when spending grew versus the comparison period. */
  spendingDeltaPct: number | null;
}

function pct(value: number): string {
  return `${Math.round(Math.abs(value) * 100)}%`;
}

export function assessFinancialMood(input: MoodInput, formatMinor: (minor: number) => string = (minor) => String(minor)): MoodAssessment {
  const savingsRate = input.incomeMinor > 0 ? (input.incomeMinor - input.expenseMinor) / input.incomeMinor : null;
  const spendingDeltaPct = input.previousExpenseMinor && input.previousExpenseMinor > 0 ? (input.expenseMinor - input.previousExpenseMinor) / input.previousExpenseMinor : null;

  if (input.accountsTotal === 0) {
    return {mood: "neutral", headline: "Nothing to watch yet", detail: "Connect an account and I can start watching your money.", savingsRate, spendingDeltaPct};
  }
  if (input.accountsFailed > 0 && input.accountsFailed >= input.accountsTotal) {
    return {mood: "concerned", headline: "I'm flying blind", detail: "Every connected account is failing to sync, so these figures may be stale.", savingsRate, spendingDeltaPct};
  }

  const overspending = input.expenseMinor > input.incomeMinor && input.incomeMinor > 0;
  const spendingSurge = spendingDeltaPct !== null && spendingDeltaPct >= 0.25;

  if (overspending && spendingSurge) {
    return {mood: "alarmed", headline: "Overspending alert", detail: `You spent ${formatMinor(input.expenseMinor - input.incomeMinor)} more than you earned, and spending is up ${pct(spendingDeltaPct ?? 0)} on the previous period.`, savingsRate, spendingDeltaPct};
  }
  if (overspending) {
    return {mood: "concerned", headline: "Spending ran ahead of income", detail: `Outgoings exceeded income by ${formatMinor(input.expenseMinor - input.incomeMinor)} this period.`, savingsRate, spendingDeltaPct};
  }
  if (spendingSurge) {
    return {mood: "watchful", headline: "Spending is climbing", detail: `Spending is up ${pct(spendingDeltaPct ?? 0)} versus the previous period. Worth a look before it becomes a habit.`, savingsRate, spendingDeltaPct};
  }
  if (savingsRate !== null && savingsRate >= 0.2) {
    return {mood: "thriving", headline: "You're on track", detail: `Your savings rate is ${pct(savingsRate)}. Keep going.`, savingsRate, spendingDeltaPct};
  }
  if (savingsRate !== null && savingsRate > 0) {
    return {mood: "content", headline: "Steady as she goes", detail: `You're saving ${pct(savingsRate)} of what comes in.`, savingsRate, spendingDeltaPct};
  }
  return {mood: "neutral", headline: "Quiet period", detail: "Not much movement to judge yet. Ask me anything about what's there.", savingsRate, spendingDeltaPct};
}

export interface MoodMoneyContext {
  currency: string;
  format: (value: Money) => string;
}
